/**
 * Sample Data Utility for Dashboard V2 Widgets
 * 
 * Provides sample data fallback when API endpoints return no data.
 * This ensures charts always display something useful for demonstration purposes.
 */

import sampleChartData from "./sampleChartData.json";

// Helper function to check if API data is empty or has no meaningful values
export function isDataEmpty(data: any): boolean {
  if (!data) return true;
  
  // Check for empty object
  if (typeof data === 'object' && Object.keys(data).length === 0) return true;
  
  // Check for categories/buckets with all zeros
  if (data.categories) {
    const values = Object.values(data.categories) as number[];
    return values.every(v => v === 0 || v === null || v === undefined);
  }
  
  if (data.buckets) {
    const bucketValues = Object.values(data.buckets).map((b: any) => 
      typeof b === 'object' ? b.count || 0 : b
    ) as number[];
    return bucketValues.every(v => v === 0);
  }
  
  if (data.bands) {
    const values = Object.values(data.bands) as number[];
    return values.every(v => v === 0 || v === null || v === undefined);
  }
  
  if (Array.isArray(data.sources) || Array.isArray(data.products) || Array.isArray(data.opportunities) || Array.isArray(data.locations)) {
    return data.sources?.length === 0 || data.products?.length === 0 || data.opportunities?.length === 0 || data.locations?.length === 0;
  }
  
  return false;
}

// Sample data getters for each widget type
export const getSampleData = {
  retirementReadiness: () => ({
    buckets: sampleChartData.widgetA_retirementReadiness.buckets,
    totalClients: sampleChartData.widgetA_retirementReadiness.totalClients,
  }),
  
  aumByProduct: () => ({
    products: sampleChartData.widgetB_aumByProduct.products,
    grandTotal: sampleChartData.widgetB_aumByProduct.grandTotal,
  }),
  
  contributionRate: () => ({
    bands: sampleChartData.widgetC_contributionRate.bands,
  }),
  
  socialSecurity: () => ({
    categories: sampleChartData.widgetD_socialSecurity.categories,
  }),
  
  retirementIncomeSources: () => ({
    sources: sampleChartData.widgetE_retirementIncomeSources.sources,
    totalAverage: sampleChartData.widgetE_retirementIncomeSources.sources.reduce(
      (sum, s) => sum + s.average, 0
    ) / sampleChartData.widgetE_retirementIncomeSources.sources.length,
  }),
  
  fegliRisk: () => ({
    categories: {
      low: sampleChartData.widgetF_fegliRisk.riskCategories.low,
      moderate: sampleChartData.widgetF_fegliRisk.riskCategories.moderate,
      high: sampleChartData.widgetF_fegliRisk.riskCategories.high,
    },
  }),
  
  ageDistribution: () => ({
    buckets: {
      under40: sampleChartData.widgetG_ageDistribution.ageBuckets.under40,
      "40to49": sampleChartData.widgetG_ageDistribution.ageBuckets["40to49"],
      "50to59": sampleChartData.widgetG_ageDistribution.ageBuckets["50to59"],
      "60to69": sampleChartData.widgetG_ageDistribution.ageBuckets["60to69"],
      "70plus": sampleChartData.widgetG_ageDistribution.ageBuckets["70plus"],
    },
  }),
  
  rolloverOpportunity: () => ({
    opportunities: sampleChartData.widgetH_rolloverOpportunity.opportunities,
  }),
  
  employerMatch: () => ({
    categories: sampleChartData.widgetI_employerMatch.categories,
  }),
  
  locationInsights: () => ({
    locations: sampleChartData.widgetJ_locationInsights.locations,
  }),
  
  planTypeSpread: () => {
    const fersCount = sampleChartData.widgetK_planTypeSpread.categories.find(c => c.planType === "FERS")?.count || 0;
    const csrsCount = sampleChartData.widgetK_planTypeSpread.categories.find(c => c.planType === "CSRS")?.count || 0;
    const k401Count = sampleChartData.widgetK_planTypeSpread.categories.find(c => c.planType === "401k")?.count || 0;
    const k403Count = sampleChartData.widgetK_planTypeSpread.categories.find(c => c.planType === "403b")?.count || 0;
    
    return {
      categories: {
        FERS: fersCount,
        CSRS: csrsCount,
        "401k": k401Count,
        "403b": k403Count,
        Private: k401Count + k403Count,
        NoPlan: 0,
        Unknown: 0,
      },
      fersSubtypes: sampleChartData.widgetK_planTypeSpread.fersSubtypes,
    };
  },
  
  longevityStress: () => ({
    atRiskCount: sampleChartData.widgetL_longevityStress.atRiskCount,
    fundedCount: sampleChartData.widgetL_longevityStress.fundedCount,
    avgYearsShort: sampleChartData.widgetL_longevityStress.avgYearsShort,
    totalClients: sampleChartData.widgetL_longevityStress.totalClients,
  }),
};
